import { useEffect, useState } from "react";
import type { ReactNode } from "react";

import { setActiveTenantId } from "@/lib/api";
import {
  DEFAULT_TENANT_CONTEXT,
  TenantContextProvider,
  type MarketScope,
  type TenantContextValue,
  type TenantResolution,
} from "./TenantContextProvider";

// SFWL-002 subdomain routing + branded theme swap.
//
// The Next.js edge middleware used to resolve the tenant from the request
// Host header, cache the white-label payload in an httpOnly cookie, and
// inject `x-tenant-id` on proxied API requests. In the Vite SPA the same
// resolution happens client-side: read the subdomain from
// `window.location.hostname`, call the by-subdomain endpoint (2s timeout),
// cache the payload in sessionStorage (5 min TTL, keyed by subdomain), and
// register the active tenant id with the API client so every request
// carries `x-tenant-id`.

const TENANT_STORAGE_KEY = "tenant";
const TENANT_CACHE_TTL_MS = 5 * 60 * 1000;
const TENANT_FETCH_TIMEOUT_MS = 2_000;
const RESERVED_SUBDOMAINS = new Set(["app", "www"]);

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3001";

const MARKET_SCOPES: ReadonlySet<MarketScope> = new Set(["futures", "crypto", "all"]);

interface TenantTheme {
  primary?: string;
  secondary?: string;
  accent?: string;
  background?: string;
  foreground?: string;
  fontFamily?: string;
}

interface ResolvedTenant {
  id: string;
  subdomain: string;
  displayName: string | null;
  logoUrl: string | null;
  theme: TenantTheme | null;
  disclosureFooter: string | null;
  marketScope: string;
}

interface CachedTenant {
  tenant: ResolvedTenant;
  expiresAt: number;
}

export function extractSubdomain(hostname: string | null): string | null {
  if (!hostname) return null;
  const host = hostname.split(":")[0];
  const parts = host.split(".");
  if (parts.length < 3) return null;
  const sub = parts[0].toLowerCase();
  if (!sub || RESERVED_SUBDOMAINS.has(sub)) return null;
  return sub;
}

function readCachedTenant(subdomain: string): ResolvedTenant | null {
  try {
    const raw = window.sessionStorage.getItem(TENANT_STORAGE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw) as CachedTenant;
    if (!cached?.tenant?.id || cached.tenant.subdomain !== subdomain) return null;
    if (typeof cached.expiresAt !== "number" || cached.expiresAt <= Date.now()) {
      window.sessionStorage.removeItem(TENANT_STORAGE_KEY);
      return null;
    }
    return cached.tenant;
  } catch {
    return null;
  }
}

function cacheTenant(tenant: ResolvedTenant): void {
  try {
    const cached: CachedTenant = {
      tenant,
      expiresAt: Date.now() + TENANT_CACHE_TTL_MS,
    };
    window.sessionStorage.setItem(TENANT_STORAGE_KEY, JSON.stringify(cached));
  } catch {
    // Storage unavailable (private mode etc.) — resolution still works per load.
  }
}

function clearCachedTenant(): void {
  try {
    window.sessionStorage.removeItem(TENANT_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
}

// SFWL-017 Codex P1 fix: distinguish "fetch succeeded" from "fetch failed"
// so the caller can set resolution to "resolved" vs "failed" — previously
// both collapsed to `null` and the caller had no way to tell them apart,
// which is exactly how the gate stayed silently open on fetch failure.
type ResolveResult = { status: "ok"; tenant: ResolvedTenant } | { status: "error" };

async function resolveTenant(subdomain: string): Promise<ResolveResult> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), TENANT_FETCH_TIMEOUT_MS);
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/v1/tenants/by-subdomain/${encodeURIComponent(subdomain)}`,
      {
        headers: { accept: "application/json" },
        signal: controller.signal,
      }
    );
    if (!response.ok) return { status: "error" };
    const dto = await response.json();
    return {
      status: "ok",
      tenant: {
        id: dto.id,
        subdomain,
        displayName: dto.displayName ?? null,
        logoUrl: dto.logoUrl ?? null,
        theme: dto.theme ?? null,
        disclosureFooter: dto.disclosureFooter ?? null,
        marketScope: dto.marketScope ?? "all",
      },
    };
  } catch {
    return { status: "error" };
  } finally {
    clearTimeout(timeout);
  }
}

function toContextValue(
  tenant: ResolvedTenant | null,
  resolution: TenantResolution
): TenantContextValue {
  if (!tenant) return { ...DEFAULT_TENANT_CONTEXT, resolution };
  const scope =
    tenant.marketScope && MARKET_SCOPES.has(tenant.marketScope as MarketScope)
      ? (tenant.marketScope as MarketScope)
      : "all";
  return {
    id: tenant.id ?? null,
    displayName: tenant.displayName ?? null,
    logoUrl: tenant.logoUrl ?? null,
    disclosureFooter: tenant.disclosureFooter ?? null,
    marketScope: scope,
    resolution,
  };
}

function buildThemeStyle(tenant: ResolvedTenant | null): string | null {
  const theme = tenant?.theme;
  if (!theme) return null;
  const entries: Array<[string, string | undefined]> = [
    ["--tenant-primary", theme.primary],
    ["--tenant-secondary", theme.secondary],
    ["--tenant-accent", theme.accent],
    ["--tenant-background", theme.background],
    ["--tenant-foreground", theme.foreground],
    ["--tenant-font-family", theme.fontFamily],
  ];
  const decls = entries
    .filter((entry): entry is [string, string] => Boolean(entry[1]))
    .map(([k, v]) => `${k}: ${v};`)
    .join(" ");
  return decls ? `:root { ${decls} }` : null;
}

export function TenantThemeProvider({ children }: { children: ReactNode }) {
  const [tenant, setTenant] = useState<ResolvedTenant | null>(() => {
    if (typeof window === "undefined") return null;
    const subdomain = extractSubdomain(window.location.hostname);
    if (!subdomain) return null;
    return readCachedTenant(subdomain);
  });

  // SFWL-017 Codex P1 fix: lifecycle tracked alongside `tenant`, initialized
  // synchronously from the same subdomain/cache read above (no
  // setState-in-effect needed for the initial value — this mirrors the
  // existing lazy-initializer pattern on `tenant`).
  const [resolution, setResolution] = useState<TenantResolution>(() => {
    if (typeof window === "undefined") return "default";
    const subdomain = extractSubdomain(window.location.hostname);
    if (!subdomain) return "default";
    return readCachedTenant(subdomain) ? "resolved" : "pending";
  });

  useEffect(() => {
    const subdomain = extractSubdomain(window.location.hostname);

    if (!subdomain) {
      // Mirrors the middleware clearing the cookie when no subdomain present.
      clearCachedTenant();
      setResolution("default");
      return;
    }

    if (tenant?.subdomain === subdomain) {
      setResolution("resolved");
      return;
    }

    const cached = readCachedTenant(subdomain);
    if (cached) {
      setTenant(cached);
      setResolution("resolved");
      return;
    }

    setResolution("pending");

    let cancelled = false;
    void resolveTenant(subdomain).then((result) => {
      if (cancelled) return;
      if (result.status === "ok") {
        cacheTenant(result.tenant);
        setTenant(result.tenant);
        setResolution("resolved");
      } else {
        // Fail closed (Codex P1): do NOT fall through to a silent "all"
        // default — a futures subdomain whose fetch errored/timed out must
        // keep the CFTC gate closed, not open. `tenant` stays null (still
        // renders DEFAULT_TENANT_CONTEXT's display values), but `resolution`
        // records the failure so consumers know not to trust marketScope.
        setResolution("failed");
      }
    });
    return () => {
      cancelled = true;
    };
  }, [tenant]);

  // Mirrors the middleware forwarding `x-tenant-id` on proxied requests.
  useEffect(() => {
    setActiveTenantId(tenant?.id ?? null);
    return () => setActiveTenantId(null);
  }, [tenant]);

  const value = toContextValue(tenant, resolution);
  const themeCss = buildThemeStyle(tenant);

  return (
    <>
      {themeCss ? <style>{themeCss}</style> : null}
      <TenantContextProvider value={value}>{children}</TenantContextProvider>
    </>
  );
}
